import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, Search, ChevronDown, ChevronRight } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { ModAcademyBuilder } from "@/components/mod/ModAcademyBuilder";
import { Input } from "@/components/ui/input";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("hu-HU", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "–");
const mins = (s) => `${Math.round((s || 0) / 60)} perc`;

function LearnerRow({ u, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`learner-row-${u.id}`}>
        <td className="px-3 py-2.5"><button onClick={() => setOpen(!open)} data-testid={`learner-expand-${u.id}`} className="flex items-center gap-1.5 text-left text-zinc-100">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{u.email}</button>{u.name && <div className="pl-5 text-[12px] text-zinc-500">{u.name}</div>}</td>
        <td className="px-3 py-2.5 text-zinc-400">{fmt(u.last_seen)}</td>
        <td className="px-3 py-2.5 text-zinc-300">{u.where || "–"}</td>
        <td className="px-3 py-2.5"><div className="flex items-center gap-2"><div className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-800"><div className="h-full bg-blue-500" style={{ width: `${u.overall_pct}%` }} /></div><span className="text-zinc-300">{u.overall_pct}%</span></div></td>
        <td className="px-3 py-2.5 text-zinc-300">{u.avg_score != null ? `${u.avg_score}%` : "–"}</td>
        <td className="px-3 py-2.5 text-zinc-400">{u.xp} XP · {mins(u.seconds)}</td>
        <td className="px-3 py-2.5 font-mono text-[12px] text-[#FFCC00]">{u.certificate || "–"}</td>
        <td className="px-3 py-2.5 text-right"><button data-testid={`learner-delete-${u.id}`} onClick={() => onDelete(u)} className="text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button></td>
      </tr>
      {open && (
        <tr className="border-b border-zinc-800/60 bg-zinc-950/60"><td colSpan={8} className="px-4 py-3">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {u.scores.map((s) => (
              <div key={s.id} className={`rounded-md border px-3 py-2 text-[12px] ${s.passed ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-800"}`}>
                <div className="font-semibold text-zinc-200">{s.title}</div>
                <div className="text-zinc-400">{s.points != null ? `${s.points}/50 pont` : s.best != null ? `Legjobb: ${s.best}% · ${s.attempts} próbálkozás` : "Nincs kitöltve"}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[12px] text-zinc-500">Szint: {u.level} · Regisztrált: {fmt(u.created_at)}</div>
        </td></tr>
      )}
    </>
  );
}

function Learners() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/academy/admin/learners").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e?.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const del = async (u) => {
    if (!window.confirm(`Törlöd ${u.email} tanulót és minden haladását?`)) return;
    await api.delete(`/academy/admin/learners/${u.id}`);
    toast.success("Tanuló törölve");
    load();
  };
  if (!rows) return <Loader2 className="animate-spin text-zinc-500" />;
  const list = rows.filter((r) => `${r.email} ${r.name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="academy-learners">
      <div className="mb-3 flex items-center gap-3">
        <div className="relative w-full max-w-xs"><Search size={14} className="absolute left-3 top-3 text-zinc-500" />
          <Input data-testid="learners-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés email / név" className="h-9 border-zinc-800 bg-zinc-950 pl-8 text-[13px]" /></div>
        <span className="text-[13px] text-zinc-500">{rows.length} tanuló · {rows.filter((r) => r.certificate).length} tanúsítvány</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full min-w-[900px] text-left text-[13px]">
          <thead className="bg-zinc-900 text-[12px] uppercase tracking-wider text-zinc-500">
            <tr>{["Email", "Utolsó látogatás", "Hol tart", "Haladás", "Átlag", "XP · idő", "Tanúsítvány", ""].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>{list.map((u) => <LearnerRow key={u.id} u={u} onDelete={del} />)}</tbody>
        </table>
        {!list.length && <div className="p-6 text-center text-[13px] text-zinc-500">Még nincs tanuló.</div>}
      </div>
    </div>
  );
}

export function ModAcademy() {
  const [tab, setTab] = useState("learners");
  return (
    <div className="space-y-5" data-testid="mod-academy">
      <div className="flex gap-2">
        {[["learners", "Tanulók"], ["builder", "Kurzusszerkesztő"]].map(([k, l]) => (
          <button key={k} data-testid={`academy-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-md px-3 py-1.5 text-[13px] ${tab === k ? "bg-blue-600 text-white" : "border border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}>{l}</button>
        ))}
        <a href="/academy" target="_blank" rel="noreferrer" className="ml-auto self-center text-[13px] text-blue-400 hover:underline">Akadémia megnyitása ↗</a>
      </div>
      {tab === "learners" ? <Learners /> : <ModAcademyBuilder />}
    </div>
  );
}
